import React from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { useTranslation } from '@/store/languageStore';
import type { ProjectTask, ProjectSource, TaskStatus } from '@/types/projects';
import { TASK_STATUSES } from '@/types/projects';
import { projectApi } from '@/services/projects/projectApi';
import { PlusIcon } from '@heroicons/react/24/outline';

interface Props {
  source: ProjectSource;
  projectId: number;
  tasks: ProjectTask[];
  onRefresh: () => void;
  onEditTask: (task?: ProjectTask) => void;
}

const priorityColor: Record<string, string> = {
  urgent: 'border-l-red-500',
  high: 'border-l-orange-500',
  normal: 'border-l-blue-400',
  low: 'border-l-gray-400',
};

export const ProjectKanban: React.FC<Props> = ({ source, tasks, onRefresh, onEditTask }) => {
  const { t } = useTranslation();

  const columns = TASK_STATUSES.filter((s) => s !== 'cancelled');

  const tasksByStatus = columns.reduce((acc, status) => {
    acc[status] = tasks
      .filter((t) => t.status === status)
      .sort((a, b) => a.kanban_sort - b.kanban_sort);
    return acc;
  }, {} as Record<TaskStatus, ProjectTask[]>);

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;
    const taskId = parseInt(result.draggableId);
    const newStatus = result.destination.droppableId as TaskStatus;
    const newSort = result.destination.index;

    await projectApi.updateTask(source, taskId, {
      status: newStatus,
      kanban_sort: newSort,
    });
    onRefresh();
  };

  return (
    <div className="overflow-x-auto pb-4">
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="flex gap-4 min-w-max">
          {columns.map((status) => (
            <div key={status} className="w-72 flex-shrink-0">
              <div className="flex items-center justify-between mb-2 px-1">
                <h3 className="font-semibold text-sm text-textDefault">
                  {t(`projects.taskStatus.${status}`)}
                </h3>
                <span className="text-xs text-textSecondary bg-bgElevated px-2 py-0.5 rounded-full">
                  {tasksByStatus[status]?.length || 0}
                </span>
              </div>
              <Droppable droppableId={status}>
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="bg-bgElevated rounded-lg p-2 min-h-[200px] border border-border"
                  >
                    {(tasksByStatus[status] || []).map((task, index) => (
                      <Draggable key={task.id} draggableId={String(task.id)} index={index}>
                        {(dragProvided) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                            onClick={() => onEditTask(task)}
                            className={`mb-2 p-3 bg-bgBase rounded-lg border border-border border-l-4 cursor-pointer hover:shadow-sm ${priorityColor[task.priority] || ''}`}
                          >
                            <p className="text-sm font-medium text-textDefault">{task.title}</p>
                            {task.subtasks && task.subtasks.length > 0 && (
                              <p className="text-xs text-textSecondary mt-1">
                                {task.subtasks.filter((s) => s.is_done).length}/{task.subtasks.length}{' '}
                                {t('projects.subtasks').toLowerCase()}
                              </p>
                            )}
                            {task.assigned_to_name && (
                              <p className="text-xs text-textSecondary mt-1">{task.assigned_to_name}</p>
                            )}
                            {task.planned_end && (
                              <p className="text-xs text-textSecondary mt-0.5">{task.planned_end}</p>
                            )}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          ))}
        </div>
      </DragDropContext>
      <button
        type="button"
        onClick={() => onEditTask(undefined)}
        className="mt-3 flex items-center gap-1 text-sm text-primary hover:underline"
      >
        <PlusIcon className="w-4 h-4" />
        {t('projects.addTask')}
      </button>
    </div>
  );
};
